
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TokenAccessParams struct
 */
class TokenAccessParams {
  /**
   * Initialize struct
   * @param {!boolean=} isManager
   * @param {!string=} accessToken
   * @constructor
   */
  constructor (isManager = false, accessToken = '') {
    this.IsManager = isManager
    this.AccessToken = accessToken
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TokenAccessParams}
   * @param {!TokenAccessParams} other Other struct
   * @returns {!TokenAccessParams} This struct
   */
  copy (other) {
    if (other.IsManager != null) {
      this.IsManager = other.IsManager
    } else {
      this.IsManager = null
    }
    if (other.AccessToken != null) {
      this.AccessToken = other.AccessToken
    } else {
      this.AccessToken = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TokenAccessParams}
   * @returns {!TokenAccessParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TokenAccessParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TokenAccessParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TokenAccessParams}
   * @param {!TokenAccessParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TokenAccessParams)) {
      throw new TypeError('Instance of TokenAccessParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TokenAccessParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      IsManager: ((this.IsManager != null) ? this.IsManager : null),
      AccessToken: ((this.AccessToken != null) ? this.AccessToken : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TokenAccessParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TokenAccessParams} other Object value
   * @returns {!TokenAccessParams} Created struct
   */
  static fromObject (other) {
    return new TokenAccessParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TokenAccessParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TokenAccessParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TokenAccessParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 14105
  }
}

export { TokenAccessParams };

/**
 * Fast Binary Encoding TokenAccessParams field model
 */
class FieldModelTokenAccessParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.IsManager = new fbe.FieldModelBool(buffer, 4 + 4)
    this.AccessToken = new fbe.FieldModelString(buffer, this.IsManager.fbeOffset + this.IsManager.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTokenAccessParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTokenAccessParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.IsManager.fbeSize + this.AccessToken.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTokenAccessParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.IsManager.fbeExtra + this.AccessToken.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTokenAccessParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTokenAccessParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTokenAccessParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 14105
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTokenAccessParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTokenAccessParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IsManager.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsManager.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsManager.fbeSize

    if ((fbeCurrentSize + this.AccessToken.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccessToken.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccessToken.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTokenAccessParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTokenAccessParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTokenAccessParams}
   * @param {!TokenAccessParams} fbeValue Default value, defaults is new TokenAccessParams()
   * @returns {!TokenAccessParams} TokenAccessParams value
   */
  get (fbeValue = new TokenAccessParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTokenAccessParams}
   * @param {!TokenAccessParams} fbeValue TokenAccessParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IsManager.fbeSize) <= fbeStructSize) {
      fbeValue.IsManager = this.IsManager.get()
    } else {
      fbeValue.IsManager = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsManager.fbeSize

    if ((fbeCurrentSize + this.AccessToken.fbeSize) <= fbeStructSize) {
      fbeValue.AccessToken = this.AccessToken.get()
    } else {
      fbeValue.AccessToken = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccessToken.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTokenAccessParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTokenAccessParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTokenAccessParams}
   * @param {!TokenAccessParams} fbeValue TokenAccessParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTokenAccessParams}
   * @param {TokenAccessParams} fbeValue TokenAccessParams value
   */
  setFields (fbeValue) {
    this.IsManager.set(fbeValue.IsManager)
    this.AccessToken.set(fbeValue.AccessToken)
  }
}

export { FieldModelTokenAccessParams };

/**
 * Fast Binary Encoding TokenAccessParams model
 */
class TokenAccessParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTokenAccessParams(this.buffer, 4)
  }

  /**
   * Get the TokenAccessParams model
   * @this {!TokenAccessParamsModel}
   * @returns {!FieldModelTokenAccessParams} model TokenAccessParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TokenAccessParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TokenAccessParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TokenAccessParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TokenAccessParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTokenAccessParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TokenAccessParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TokenAccessParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TokenAccessParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TokenAccessParamsModel}
   * @param {!TokenAccessParams} value TokenAccessParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TokenAccessParamsModel}
   * @param {!TokenAccessParams} value TokenAccessParams value, defaults is new TokenAccessParams()
   * @return {!object} Deserialized TokenAccessParams value and its size
   */
  deserialize (value = new TokenAccessParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TokenAccessParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TokenAccessParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TokenAccessParamsModel}
   * @param {!number} prev Previous TokenAccessParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TokenAccessParamsModel };
